import React, { useState, useEffect } from 'react';
import { ErrorBoundary } from './ErrorBoundary';

interface AsyncErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const AsyncErrorBoundary: React.FC<AsyncErrorBoundaryProps> = ({ 
  children, 
  fallback 
}) => {
  const [asyncError, setAsyncError] = useState<Error | null>(null);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      // Convert promise rejection to error for error boundary
      const error = event.reason instanceof Error 
        ? event.reason 
        : new Error(String(event.reason));
      
      setAsyncError(error);
      event.preventDefault(); // Prevent default browser behavior
    };

    // Handle async errors in event handlers
    const handleError = (event: ErrorEvent) => {
      console.error('Async error:', event.error);
      setAsyncError(event.error);
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  // Throw async error to be caught by error boundary
  if (asyncError) {
    throw asyncError;
  }

  const handleReset = () => {
    setAsyncError(null);
    setResetKey(prev => prev + 1);
  };

  return (
    <ErrorBoundary 
      fallback={fallback}
      resetKeys={[resetKey]}
      onError={handleReset}
    >
      {children}
    </ErrorBoundary>
  );
};